import React, { useState, useEffect } from 'react';
import CreateActivityForm from './CreateActivityForm';

const buildings = [
    { value: '1', label: 'Building 1' },
    { value: '2', label: 'Building 2' },
    { value: '3', label: 'Building 3' },
    { value: '4', label: 'Building 4' },
];

const banks = [
    { value: '1', label: 'Passenger' },
];

const deviceOptions = [
    { value: '1', label: 'Elevator 1' },
];

function Settings() {
    const [selectedBuilding, setSelectedBuilding] = useState('');
    const [selectedBank, setSelectedBank] = useState('');
    const [selectedDevice, setSelectedDevice] = useState('');
    const [activeTab, setActiveTab] = useState('deviceServices');
    const [fromDateTime, setFromDateTime] = useState('');
    const [toDateTime, setToDateTime] = useState('');
    const [showCreateActivityForm, setShowCreateActivityForm] = useState(false);

    useEffect(() => {
        const today = new Date();
        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0);
        const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59);
        const pad = (num) => num.toString().padStart(2, '0');
        const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        setFromDateTime(fmt(startOfDay));
        setToDateTime(fmt(endOfDay));
    }, []);

    const tabs = [
        { key: 'deviceServices', label: 'Device Services' },
        { key: 'operatingHours', label: 'Operating Hours' },
        { key: 'criticalHours', label: 'Critical Hours' },
    ];

    const selectClass = "w-full bg-slate-900/60 border border-slate-600/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50";
    const inputClass = "w-full bg-slate-900/60 border border-slate-600/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 [&::-webkit-calendar-picker-indicator]:invert";
    const labelClass = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1";

    return (
        <div className="bg-[#0F172A] p-6 overflow-y-auto h-full w-full flex flex-col">
            <div className="space-y-6">
                {/* Page Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white">Settings</h1>
                        <p className="text-sm text-slate-400 mt-1">Configure building, bank, and device settings</p>
                    </div>
                    <span className="text-3xl">⚙️</span>
                </div>

                {/* Date/Time Range */}
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                    <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">Date & Time Range</h2>
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="flex-1 min-w-[200px]">
                            <label className={labelClass}>From</label>
                            <input type="datetime-local" value={fromDateTime} onChange={(e) => setFromDateTime(e.target.value)} className={inputClass} />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <label className={labelClass}>To</label>
                            <input type="datetime-local" value={toDateTime} onChange={(e) => setToDateTime(e.target.value)} className={inputClass} />
                        </div>
                    </div>
                </div>

                {/* Selectors */}
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                    <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">Select Equipment</h2>
                    <div className="flex flex-wrap gap-4">
                        <div className="flex-1 min-w-[180px]">
                            <label className={labelClass}>Building</label>
                            <select value={selectedBuilding} onChange={(e) => { setSelectedBuilding(e.target.value); setSelectedBank(''); setSelectedDevice(''); }} className={selectClass}>
                                <option value="">Select Building</option>
                                {buildings.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                            </select>
                        </div>
                        {selectedBuilding && (
                            <div className="flex-1 min-w-[180px]">
                                <label className={labelClass}>Bank ID & Location</label>
                                <select value={selectedBank} onChange={(e) => { setSelectedBank(e.target.value); setSelectedDevice(''); }} className={selectClass}>
                                    <option value="">Select Bank</option>
                                    {banks.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                                </select>
                            </div>
                        )}
                        {selectedBank && (
                            <div className="flex-1 min-w-[180px]">
                                <label className={labelClass}>Device</label>
                                <select value={selectedDevice} onChange={(e) => setSelectedDevice(e.target.value)} className={selectClass}>
                                    <option value="">Select Device</option>
                                    {deviceOptions.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tabs + Table — only when device selected */}
                {selectedDevice && (
                    <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden">
                        {/* Tab bar */}
                        <div className="flex border-b border-slate-700/50">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`flex-1 py-3 px-4 text-sm font-semibold transition ${
                                        activeTab === tab.key
                                            ? 'bg-slate-700/60 text-cyan-400 border-b-2 border-cyan-400'
                                            : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Tab content */}
                        <div className="p-5">
                            {activeTab === 'deviceServices' && (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-700/50">
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Date</th>
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Device</th>
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Device Status</th>
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Description</th>
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Username</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td colSpan={6} className="py-8 text-center text-slate-500">No device service records found</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {activeTab === 'operatingHours' && (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-700/50">
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Day</th>
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Hour Interval</th>
                                                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td colSpan={3} className="py-8 text-center text-slate-500">No operating hours configured</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {activeTab === 'criticalHours' && (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-700/50">
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Day</th>
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Morning</th>
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Lunch</th>
                                                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Evening</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td colSpan={4} className="py-8 text-center text-slate-500">No critical hours configured</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Create Activity button */}
                        <div className="px-5 pb-5">
                            <button
                                onClick={() => setShowCreateActivityForm(true)}
                                className="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold rounded-lg transition"
                            >
                                + Create Activity
                            </button>
                        </div>
                    </div>
                )}

                {/* Prompt when no device selected */}
                {!selectedDevice && (
                    <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-10 text-center">
                        <span className="text-4xl">🏗️</span>
                        <p className="text-lg font-semibold text-slate-300 mt-3">Select a Building, Bank, and Device</p>
                        <p className="text-sm text-slate-500 mt-1">Device services, operating hours, and critical hours will appear here</p>
                    </div>
                )}

                {/* Create Activity Modal */}
                {showCreateActivityForm && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreateActivityForm(false)}>
                        <div className="bg-slate-800 border border-slate-700/50 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between p-5 border-b border-slate-700/50">
                                <h2 className="text-lg font-bold text-white">Create Activity</h2>
                                <button onClick={() => setShowCreateActivityForm(false)} className="text-slate-400 hover:text-white text-xl transition">&times;</button>
                            </div>
                            <div className="p-5">
                                <CreateActivityForm />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Settings;
